import React from 'react';

import { css } from '@emotion/react';
import { NeutralColorToken, TextButton } from '@robinhood/design';
import { graphql } from 'relay-runtime';

import { CreditFrozenPageMutation as CreditFrozenPageMutationType } from 'src/clients/Network/gql/build/CreditFrozenPageMutation.graphql';
import Button from 'src/components/creditApplication/Button';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import GetHelpTextButton from 'src/components/creditApplication/GetHelpTextButton';
import { NavigationContext } from 'src/components/creditApplication/NavigationProvider';
import usePollStatus, { TERMINAL_STATUSES } from 'src/hooks/usePollStatus';
import { showGlobalErrorAlert, useGlobalStore } from 'src/state/global';
import { useAsyncMutation } from 'src/utils/graphql';

const CreditFrozenUrl = 'https://www.experian.com/freeze/center.html';

const CreditFrozenPageMutation = graphql`
  mutation CreditFrozenPageMutation($identityId: String!) {
    putIdentity(q: { identity: { id: $identityId, socialSecurityNumbers: [{ entity: { sync: true } }] } }) {
      data {
        syncTaskId
      }
      errors
    }
  }
`;

const CreditFrozenPage = () => {
  const { underwrite, underwriteLoading } = React.useContext(NavigationContext);
  const identityId = useGlobalStore((s) => s.identityId);
  const [commit, commitLoading] = useAsyncMutation<CreditFrozenPageMutationType>(CreditFrozenPageMutation);
  const [taskStatus, initTask, isPolling] = usePollStatus(30);
  const loading = commitLoading || underwriteLoading || isPolling;

  const onRerun = React.useCallback(async () => {
    if (!!identityId) {
      const res = await commit({
        variables: {
          identityId,
        },
      });
      if (res.putIdentity.errors.length > 0) {
        showGlobalErrorAlert();
      } else if (!!res.putIdentity.data?.syncTaskId) {
        initTask(res.putIdentity.data.syncTaskId);
      }
    }
  }, [identityId]);

  React.useEffect(() => {
    if (TERMINAL_STATUSES.has(taskStatus)) {
      if (taskStatus === 'SUCCEEDED') {
        underwrite();
      } else if (taskStatus === 'FAILED') {
        showGlobalErrorAlert();
      }
    }
  }, [taskStatus, underwrite]);

  return (
    <CreditApplicationPageLayout
      showLogo={false}
      icon='Unlock24'
      title='Your credit&nbsp;report is&nbsp;frozen'
      subtitle={
        <>
          You can{' '}
          <TextButton
            variant='inline'
            type='anchor'
            href={CreditFrozenUrl}
            target='_blank noopener noreferrer'
            label='unfreeze your credit report here'
            color={NeutralColorToken.Fg2}
          >
            unfreeze your credit report here
          </TextButton>
          . Once your report is unfrozen, please press below to continue.
        </>
      }
    >
      <div css={root}>
        <Button
          label='Check again'
          icon='Repeat16'
          state={loading ? 'loading' : 'default'}
          width='full'
          type='submit'
          onClick={onRerun}
        />
        <GetHelpTextButton />
      </div>
    </CreditApplicationPageLayout>
  );
};

const root = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 24px;
  gap: 24px;
  text-align: center;
`;

export default CreditFrozenPage;
